import classNames from "classnames"
import React, { useContext, useEffect, useState } from "react"

import { DEFAULT_STOREFRONT } from "../../cart/constants"
import { useFetchConsolidatedCartProducts } from "../../cart/hooks"
import { CartContext } from "../../cart/store"
import { getCookie } from "../../utils/analytics"
import {
  fetchCartId,
  fetchCartProducts,
  localStorageAvailable,
  shop,
  storeCartId,
  storeCartProducts,
  storeCartPromoCode,
  StoredBundle,
  StoredItem,
  StoredItemBase,
  StoredProduct,
} from "../../utils/shop"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import CloseIcon from "@components/elements/V2/Icons/CloseIcon"
import Section from "@components/elements/V2/Section"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"

const ReminderBanner = () => {
  const [isClosed, setIsClosed] = useState(false)
  const [bannerReady, setBannerReady] = useState(false)
  const cartContext = useContext(CartContext)
  const urlParams = new URLSearchParams(location.search)
  const promotionParam = urlParams.get("promotion")
  const fetchProducts = useFetchConsolidatedCartProducts()
  const cartActions = useContext(CartContext)

  const pendingSignupId = localStorageAvailable()
    ? getCookie("pending_signup_id")
    : undefined

  useEffect(() => {
    // We pull in the existing cart from a hello signup if
    // there isnt currently a signup on yoco-com
    const fetchSignupCart = async () => {
      const validExistingCart = !!fetchCartId() && !!fetchCartProducts()?.length

      // Only set to new cart if there is no existing cart or its empty
      if (pendingSignupId && !validExistingCart) {
        const signup = await shop.signups.fetchSignup(pendingSignupId)
        const signupCart = await shop.carts.get(signup.cart_uuid)

        // These items still need storyblok uuuid's
        const newStoredItems = signupCart.items.reduce(
          (acc: StoredItem[], item) => {
            if (item.product) {
              return [
                ...acc,
                {
                  sku: item.product.sku,
                  quantity: item.quantity,
                  type: "product",
                  storefront: DEFAULT_STOREFRONT,
                } as StoredProduct,
              ]
            } else if (item.bundle) {
              return [
                ...acc,
                {
                  bundleCode: item.bundle.bundle_code,
                  quantity: item.quantity,
                  type: "bundle",
                  storefront: DEFAULT_STOREFRONT,
                } as StoredBundle,
              ]
            }
            return acc
          },
          []
        )

        const consolidatedProducts = fetchProducts(newStoredItems, {
          findBySkuOrCode: true,
        })

        // Add storyblok uuid's so that we can store in localstorage
        const storedCartProducts = newStoredItems.reduce(
          (acc: StoredItemBase[], newStoredItem) => {
            const consolidatedProduct = consolidatedProducts.find(
              (consolidatedProduct) =>
                consolidatedProduct.type === "product"
                  ? consolidatedProduct.sku
                  : consolidatedProduct.bundleCode
            )

            if (consolidatedProduct) {
              return [
                ...acc,
                {
                  ...newStoredItem,
                  id: consolidatedProduct.id,
                },
              ]
            }

            return acc
          },
          []
        )

        cartActions.setCartId(signup.cart_uuid)
        cartActions.setProducts(consolidatedProducts)

        storeCartId(signup.cart_uuid)
        storeCartProducts(storedCartProducts)

        if (promotionParam && typeof promotionParam === "string") {
          storeCartPromoCode(promotionParam)
        }
      }

      setBannerReady(true)
    }

    fetchSignupCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSignupId])

  if (!pendingSignupId || !bannerReady) {
    return null
  }

  const firstCartItemImage =
    cartContext?.products && cartContext.products.length > 0
      ? typeof cartContext.products[0].image === "string"
        ? cartContext.products[0].image
        : cartContext.products[0].image?.filename
      : null

  return (
    <Section
      className={classNames(
        "flex min-h-64 flex-row gap-12 bg-charcoal py-12 sm:items-center sm:gap-16",
        {
          hidden: isClosed,
        }
      )}
    >
      {firstCartItemImage && (
        <div className="mt-1 flex h-48 min-w-48 rounded-[4px] bg-black sm:mt-0">
          <GatsbyStoryblokImage
            image={firstCartItemImage}
            alt={`${cartContext.products[0].name} image`}
            height={48}
            width={48}
          />
        </div>
      )}
      <div className="flex w-full flex-col justify-between gap-10 sm:flex-row sm:gap-x-24">
        <div className="flex flex-col gap-y-4 md:gap-0">
          <div className="flex items-center justify-between">
            <Typography
              text="Every missed sale adds up"
              size="body-sm"
              font="grotesk"
              weight="medium"
              color={Color.White}
            />
            <div className="h-12 w-12 cursor-pointer sm:hidden">
              <CloseIcon
                width="12px"
                height="12px"
                hideBorder
                strokeWidth="2"
                strokeColor={Color.White}
                onClick={() => setIsClosed(true)}
              />
            </div>
          </div>
          <Typography
            text="With fast transactions, free reports on sales, staff and top-selling products, Yoco helps over 200,000 businesses get paid & grow."
            size="body-sm"
            font="grotesk"
            weight="book"
            color={Color.White}
            className="opacity-80"
          />
        </div>
        <div className="flex items-center gap-16">
          <PillButton
            style="bordered"
            size="small"
            color={Color.Blue}
            text="Complete signup"
            hideIcon
            linkUrl={`${process.env.GATSBY_SHOP_SIGNUP_URL}/${pendingSignupId}/continue`}
          />
          <div className="hidden h-24 w-24 cursor-pointer sm:block">
            <CloseIcon
              width="24px"
              height="24px"
              hideBorder
              strokeWidth="2"
              strokeColor={Color.White}
              onClick={() => setIsClosed(true)}
            />
          </div>
        </div>
      </div>
    </Section>
  )
}

export default ReminderBanner
